import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation, useSearchParams, useNavigate, useParams } from 'react-router-dom';
import './App.css';
import Hero from './components/Hero';
import HowItWorks from './components/HowItWorks';
import SamplePreview from './components/SamplePreview';
import WhyPeopleLoveIt from './components/WhyPeopleLoveIt';
import BookPreview from './components/BookPreview';
import Pricing from './components/Pricing';
import GiftSection from './components/GiftSection';
import FAQ from './components/FAQ';
import FinalCTA from './components/FinalCTA';
import Footer from './components/Footer';
import LoginPage from './components/LoginPage';
import AuthCallback from './components/AuthCallback';
import Dashboard from './components/Dashboard';
import ProtectedRoute from './components/ProtectedRoute';
import PaymentModal from './components/PaymentModal';
import PaymentSuccess from './components/PaymentSuccess';
import Questionnaire from './components/Questionnaire';
import PreviewAndEdit from './components/PreviewAndEdit';
import AutobiographyViewer from './components/AutobiographyViewer';
import HardcoverOrder from './components/HardcoverOrder';
import HardcoverOrderFlow from './components/HardcoverOrderFlow';
import HardcoverSuccess from './components/HardcoverSuccess';
import BundleOrderFlow from './components/BundleOrderFlow';
import BundleSuccess from './components/BundleSuccess';
import GiftPurchase from './components/GiftPurchase';
import GiftSuccess from './components/GiftSuccess';
import GiftRedeem from './components/GiftRedeem';
import AdminDashboard from './components/AdminDashboard';
import CommunityFeed from './components/CommunityFeed';
import PublicStory from './components/PublicStory';
import LanguageSelector from './components/LanguageSelector';
import { Toaster } from './components/ui/sonner';
import './i18n';

// Standalone autobiography view page (from dashboard)
function AutobiographyPage() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [data, setData] = useState(location.state?.autobiographyData || null);
  const [loading, setLoading] = useState(!data);

  useEffect(() => {
    if (!data && id) {
      const fetchData = async () => {
        try {
          const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
          const res = await fetch(`${BACKEND_URL}/api/autobiography/${id}`);
          if (res.ok) setData(await res.json());
          else navigate('/dashboard');
        } catch {
          navigate('/dashboard');
        } finally {
          setLoading(false);
        }
      };
      fetchData();
    }
  }, [id, data, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-100 to-purple-100 flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-purple-200 border-t-purple-500 rounded-full animate-spin" />
      </div>
    );
  }
  if (!data) return null;

  return (
    <AutobiographyViewer
      autobiographyData={data}
      onOrderHardcover={() => navigate(`/hardcover/${data.id}`)}
      onBack={() => navigate('/dashboard')}
    />
  );
}

// Main App Router that handles session_id detection
function AppRouter() {
  const location = useLocation();

  // CRITICAL: Check for session_id in URL fragment synchronously
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/dashboard" element={
        <ProtectedRoute><Dashboard /></ProtectedRoute>
      } />
      <Route path="/autobiography/:id" element={<AutobiographyPage />} />
      <Route path="/hardcover-order/:autobiographyId" element={<HardcoverOrderFlow />} />
      <Route path="/hardcover-success" element={<HardcoverSuccess />} />
      <Route path="/bundle-order/:autobiographyId" element={<BundleOrderFlow />} />
      <Route path="/bundle-success" element={<BundleSuccess />} />
      <Route path="/gift" element={<GiftPurchase />} />
      <Route path="/gift-success" element={<GiftSuccess />} />
      <Route path="/gift-redeem/:giftCode" element={<GiftRedeem />} />
      <Route path="/admin" element={<AdminDashboard />} />
      <Route path="/community" element={<CommunityFeed />} />
      <Route path="/story/:id" element={<PublicStory />} />
      <Route path="/" element={<LandingPage />} />
    </Routes>
  );
}

function LandingPage() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [currentView, setCurrentView] = useState('landing');
  const [userData, setUserData] = useState(null);
  const [autobiographyData, setAutobiographyData] = useState(null);

  useEffect(() => {
    if (searchParams.get('payment') === 'success' && searchParams.get('session_id')) return;
    if (location.state?.startQuestionnaire && location.state?.userData) {
      setUserData(location.state.userData);
      setCurrentView('questionnaire');
    }
  }, [searchParams, location.state]);

  if (searchParams.get('payment') && searchParams.get('session_id')) return <PaymentSuccess />;
  if (searchParams.get('payment') === 'cancelled') return <PaymentSuccess />;

  const handleStartClick = () => setCurrentView('payment');
  const handlePaymentSuccess = (formData) => {
    setUserData({ name: formData.name, email: formData.email });
    setCurrentView('questionnaire');
  };
  const handlePaymentCancel = () => setCurrentView('landing');
  const handleQuestionnaireComplete = (resp) => {
    setAutobiographyData(resp);
    setCurrentView('preview');
  };
  const handlePreviewFinalize = (d) => { setAutobiographyData(d); setCurrentView('viewer'); };
  const handleRegenerate = () => setCurrentView('questionnaire');
  const handleOrderHardcover = () => setCurrentView('hardcover');
  const handleBackToViewer = () => setCurrentView('viewer');

  if (currentView === 'questionnaire')
    return <Questionnaire onComplete={handleQuestionnaireComplete} userData={userData} />;
  if (currentView === 'preview')
    return <PreviewAndEdit autobiographyData={autobiographyData} onFinalize={handlePreviewFinalize} onRegenerate={handleRegenerate} />;
  if (currentView === 'viewer')
    return <AutobiographyViewer autobiographyData={autobiographyData} onOrderHardcover={handleOrderHardcover} />;
  if (currentView === 'hardcover')
    return <HardcoverOrder autobiographyId={autobiographyData?.id} onBack={handleBackToViewer} />;

  return (
    <div className="App">
      {/* Floating Language Selector */}
      <div className="fixed top-4 right-4 z-50"><LanguageSelector /></div>
      <Hero onStartClick={handleStartClick} />
      <HowItWorks />
      <SamplePreview />
      <WhyPeopleLoveIt />
      <BookPreview />
      <Pricing onPurchaseClick={handleStartClick} />
      <GiftSection onStartClick={handleStartClick} />
      <FAQ />
      <FinalCTA onStartClick={handleStartClick} />
      <Footer />
      {currentView === 'payment' && (
        <PaymentModal onSuccess={handlePaymentSuccess} onCancel={handlePaymentCancel} />
      )}
      <Toaster />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppRouter />
    </BrowserRouter>
  );
}

export default App;
